// CRM заявок: карточка, контакты, касания, задачи, файлы, заведение и загрузка
// базы. Любая правка обновляет карточку, доску (на ней следующий шаг, теги,
// источник) и список «Мои задачи» — менеджер видит одно и то же везде.
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  CrmActivityInput,
  CrmActivityPatch,
  CrmContactInput,
  CrmContactPatch,
  CrmFileCategory,
  CrmFilePatch,
  CrmLeadCreate,
  CrmLeadImport,
  CrmLeadPatch,
  CrmTaskDueFilter,
  CrmTaskInput,
  CrmTaskPatch,
  CrmTaskScope,
} from "@osmix/contracts"
import { api, type ApplicationCard } from "@/api"
import { applicationKeys } from "./use-applications"

export const crmKeys = {
  all: ["applicationCrm"] as const,
  card: (id: string) => ["applicationCrm", "card", id] as const,
  tasksAll: ["applicationCrm", "tasks"] as const,
  tasks: (scope: CrmTaskScope, due: CrmTaskDueFilter) => ["applicationCrm", "tasks", scope, due] as const,
  duplicates: (query: DuplicateQuery) =>
    ["applicationCrm", "duplicates", query.phone ?? "", query.email ?? "", query.inn ?? "", query.excludeId ?? ""] as const,
  file: (id: string, fileId: string) => ["applicationCrm", "file", id, fileId] as const,
}

type DuplicateQuery = { phone?: string; email?: string; inn?: string; excludeId?: string }

/** Карточка уже пришла в ответе — кладём её, остальное перезапрашиваем. */
const refresh = (queryClient: QueryClient, id: string, card?: ApplicationCard) => {
  if (card) queryClient.setQueryData(crmKeys.card(id), card)
  else void queryClient.invalidateQueries({ queryKey: crmKeys.card(id) })
  void queryClient.invalidateQueries({ queryKey: applicationKeys.board })
  void queryClient.invalidateQueries({ queryKey: crmKeys.tasksAll })
}

/** Для правок, сделанных вне хуков CRM (стадия, ответственный, кабинет). */
export function useRefreshCard(id: string) {
  const queryClient = useQueryClient()
  return () => refresh(queryClient, id)
}

export function useApplicationCard(id: string) {
  return useQuery({
    queryKey: crmKeys.card(id),
    queryFn: () => api.applicationCrm.card(id),
    enabled: !!id,
  })
}

export function useUpdateLead(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: CrmLeadPatch) => api.applicationCrm.update(id, patch),
    onSuccess: (card) => refresh(queryClient, id, card),
  })
}

export function useFillCompany(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.applicationCrm.fillCompany(id),
    onSuccess: (card) => refresh(queryClient, id, card),
  })
}

export function useAddContact(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CrmContactInput) => api.applicationCrm.addContact(id, input),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useUpdateContact(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ contactId, patch }: { contactId: string; patch: CrmContactPatch }) =>
      api.applicationCrm.updateContact(id, contactId, patch),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useDeleteContact(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) => api.applicationCrm.deleteContact(id, contactId),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useAddActivity(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CrmActivityInput) => api.applicationCrm.addActivity(id, input),
    onSuccess: (card) => refresh(queryClient, id, card),
  })
}

export function useUpdateActivity(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ activityId, patch }: { activityId: string; patch: CrmActivityPatch }) =>
      api.applicationCrm.updateActivity(id, activityId, patch),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useDeleteActivity(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (activityId: string) => api.applicationCrm.deleteActivity(id, activityId),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useAddTask(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CrmTaskInput) => api.applicationCrm.addTask(id, input),
    onSettled: () => refresh(queryClient, id),
  })
}

/** Заявка — в переменных: задачу отмечают и из карточки, и из «Моих задач» на доске. */
export function useUpdateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ applicationId, taskId, patch }: { applicationId: string; taskId: string; patch: CrmTaskPatch }) =>
      api.applicationCrm.updateTask(applicationId, taskId, patch),
    onSettled: (_task, _error, { applicationId }) => refresh(queryClient, applicationId),
  })
}

export function useDeleteTask(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) => api.applicationCrm.deleteTask(id, taskId),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useCrmTasks(scope: CrmTaskScope, due: CrmTaskDueFilter) {
  return useQuery({
    queryKey: crmKeys.tasks(scope, due),
    queryFn: () => api.applicationCrm.tasks(scope, due),
    // Как доска: менеджер держит страницу открытой, сроки подходят сами.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  })
}

export function useUploadFile(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, category }: { file: File; category?: CrmFileCategory }) =>
      api.applicationCrm.uploadFile(id, file, category),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useUpdateFile(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId, patch }: { fileId: string; patch: CrmFilePatch }) =>
      api.applicationCrm.updateFile(id, fileId, patch),
    onSettled: () => refresh(queryClient, id),
  })
}

export function useDeleteFile(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (fileId: string) => api.applicationCrm.deleteFile(id, fileId),
    onSuccess: (_void, fileId) => queryClient.removeQueries({ queryKey: crmKeys.file(id, fileId) }),
    onSettled: () => refresh(queryClient, id),
  })
}

/** Байты файла с токеном консоли: превью, просмотр и скачивание. Файл не меняется — кэш вечный. */
export function useCrmFileBlob(id: string, fileId: string, enabled = true) {
  return useQuery({
    queryKey: crmKeys.file(id, fileId),
    queryFn: () => api.applicationCrm.fileBlob(id, fileId),
    enabled: enabled && !!id && !!fileId,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 10 * 60_000,
    retry: false,
  })
}

export function useCreateLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CrmLeadCreate) => api.applicationCrm.create(input),
    onSuccess: (card) => refresh(queryClient, card.application.id, card),
  })
}

/** Подсказка дублей в форме новой заявки: ищем, когда есть по чему. */
export function useLeadDuplicates(query: DuplicateQuery) {
  const phoneDigits = (query.phone ?? "").replace(/\D/g, "")
  const innDigits = (query.inn ?? "").replace(/\D/g, "")
  const enabled = phoneDigits.length >= 10 || (query.email ?? "").includes("@") || innDigits.length >= 10
  return useQuery({
    queryKey: crmKeys.duplicates(query),
    queryFn: () => api.applicationCrm.duplicates(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  })
}

export function useImportLeads() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CrmLeadImport) => api.applicationCrm.importLeads(input),
    onSuccess: (result) => {
      if (result.dryRun) return
      void queryClient.invalidateQueries({ queryKey: applicationKeys.board })
      void queryClient.invalidateQueries({ queryKey: crmKeys.tasksAll })
    },
  })
}

export function useExportLeads() {
  return useMutation({ mutationFn: () => api.applicationCrm.exportLeads() })
}
